// React
import React from "react";
import {useTranslation} from "react-i18next";

// Dayjs
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
dayjs.extend(utc);
dayjs.extend(timezone);
export const TIME_ZONE = "UTC";

// Recoil
import {useRecoilValue} from "recoil";
import {kruValuesState, resultProvidersState, useAuthenticatedUser} from "recoil/globalStates";

// Local
import {litterRegRules, dogRegRules} from "./rules";
import {countries} from "./countries";
import {Checkmark, Errormark} from "elements/BreederBadges";
import {getOrganizationById} from "api/publicRoutes";
import {imageServer} from "constants/values";
import defaultImgUrl from "images/placeholder.png";

export const ROLES = {
  basic: {power: 1},
  breeder: {power: 5},
  manager: {power: 10},
  admin: {power: 9999},
};

export const iconStyle = {
  color: "#0000008a",
  fontSize: "1.2rem",
};

// Badges
// TODO: this should be in components
export const getBadge = (breeder) => {
  if (breeder) {
    if (breeder?.certificates?.length > 0) {
      return <Checkmark />;
    } else {
      return <Errormark />;
    }
  } else {
    return " ";
  }
};

// takes in array of dog objects and a boolean, sorts then according to the given
// boolean and return the sorted array
export function sortDogsByCreationDate(dogs, sortingAsc) {
  const sortedOwnedDogs = [...dogs];

  sortedOwnedDogs.sort(function compare(a, b) {
    var dateA = dayjs(a.createdAt);
    var dateB = dayjs(b.createdAt);
    return sortingAsc ? dateB - dateA : dateA - dateB;
  });
  return sortedOwnedDogs;
}

// Get ResultProvider by ID
export function getResultProvider(id) {
  const resultProviders = useRecoilValue(resultProvidersState);
  return resultProviders.find((provider) => provider._id === id);
}

// Get KruValue's label in selected language by id
// NOTE: kruValues must be passed as a param, not fetched internally via useRecoilValue --
// this is a plain helper, not a component, and callers invoke it a variable number of times
// per render (e.g. once per coat modifier/marking in a .map()), which violates the Rules of
// Hooks if the hook lives in here (see getKruValueLabels below, which already avoided this).
export function getKruValueLabel(id, lang, kruValues) {
  if (!id) {
    return "";
  }

  const found = kruValues.find((value) => value._id === id);
  return found && getNameByLang(found, lang);
}

// Get KruValue's labels for an array in selected language by id
// Useful for arrays of KruValues, e.g. coat colors and coat types
// NOTE: kruValues needs to be passed as param since calling it in this
// function is going to cause rendering error
export function getKruValueLabels(array, lang, kruValues) {
  if (typeof array === "string") {
    array = Array(array);
  }
  if (array !== "") {
    let kruValueArray = array.map((val) => {
      const found = kruValues.find((value) => value._id === val);
      return found && found[lang].name;
    });
    return kruValueArray.join(", ");
  } else {
    return "";
  }
}

// Check if parent is not too young
export function checkAge(parent) {
  const dateParent = dayjs(parent.dateOfBirth);
  const dateToday = dayjs();
  const parentAge = dateToday.diff(dateParent, "month");

  if (parentAge < litterRegRules.parentAgeMonthsMin) {
    return {ok: false, age: parentAge};
  } else {
    return {ok: true, age: null};
  }
}

// Check if dog is not too young for registration
export function checkDogAge(dob) {
  const dateDog = dayjs(dob);
  const dateToday = dayjs();
  const dogAge = dateToday.diff(dateDog, "month");

  if (dogAge < dogRegRules.ageMonthsMin) {
    return {ok: false, age: dogAge};
  } else {
    return {ok: true, age: null};
  }
}

// Count the number of each gender to show in form UI
export function countFemales(puppies) {
  if (puppies) {
    const count = puppies.filter((pup) => pup.sex == "female");

    return count?.length;
  }
}
// Count the number of each gender to show in form UI
export function countMales(puppies) {
  if (puppies) {
    const count = puppies.filter((pup) => pup.sex == "male");

    return count?.length;
  }
}

export function getCountryByCode(code, lang) {
  if (code) {
    const country = code.toUpperCase();
    const found = countries.find((c) => c.code === country);
    if (!found) {
      return code;
    } else {
      return found[lang];
    }
  }
}

export async function getOrganizationNamesById(orgArray) {
  let promiseArray = [];
  orgArray.forEach((org) => promiseArray.push(getOrganizationById(org)));
  Promise.all(promiseArray).then((vals) => {
    const mappedValues = vals.map((v) => v.name);
    return mappedValues;
  });
}

export function getUserOrg(user) {
  if (user.organizations?.length == 1) {
    return user.organizations[0].organization;
  }
  return "";
}

// Return this organization+etc for this object,
// or undef if it is not in this org.
export function getOrgAndMoreByPrefix(obj, prefix) {
  if (!prefix) {
    return null;
  }

  return obj.organizations.find((o) => o.prefix === prefix);
  // if none matched, we'll return undefined
}

// Return this organization+etc for this object,
// or undef if it is not in this org.
export function getOrgAndMore(obj, org) {
  if (org === null || org === undefined || obj.organizations == null) {
    return null;
  }

  return obj.organizations.find((o) => o._id === org._id || o.organization?._id === org._id);
  // if none matched, we'll return undefined
}

export function getNeedsVerif(dog, prefix) {
  const orgAndMore = getOrgAndMoreByPrefix(dog, prefix);
  return (
    orgAndMore?.metadata?.verifiedObject &&
    !orgAndMore.metadata.verified
  );
};

export function compareRole(role, comparisonRole) {
  if (!role) {
    return false;
  }

  return ROLES[role].power >= ROLES[comparisonRole].power;
}

// Is the user a site-level manager in this org, or overall admin?
export function getUserManagerPlusOrg(user, org) {
  if (compareRole(user.role, "admin")) {
    return true;
  }
  const orgAndRole = getOrgAndMore(user, org);
  return (orgAndRole ? compareRole(orgAndRole.role, "manager") : false);
}

export function canEditDog(user, dog) {
  if (compareRole(user.role, "admin")) return true;

  if (dog && dog.owners && dog.owners.includes(user._id)) return true;

  if (
    dog && dog.owners &&
    dog.owners.some((owner) => owner._id === user._id)
  )
    return true;

  if (!user?.organizations || !dog?.organizations) return false;

  return user.organizations.some(userOrg =>
    dog.organizations.some(dogOrg =>
      userOrg.prefix === dogOrg.prefix && compareRole(userOrg.role, "manager")
    )
  );
}

// How much does the user need to pay to register a dog/litter?
export function getPaymentAmount(user, type, org) {
  // is user site admin? -> 0
  if (compareRole(user.role, "admin")) {
    return 0;
  }

  // If there's mo organization then you can't register a dog.
  if (org == null) {
    return null;
  }

  // is user in the organization specified? If not, error
  const orgAndRole = getOrgAndMore(user, org);
  if (orgAndRole == null) {
    throw new Error("Must be a breeder in " + org.name + " to register a " + type + " with them.");
  }
  const userOrg = orgAndRole.organization;
  const userRole = orgAndRole.role;

  // Return amount
  return userOrg.pricing[type][userRole];
}

// Should we offer to let this user register a dog in this org?
export const userCanRegister = (org) => {
  const user = useAuthenticatedUser();

  if (user === null) {
    return false;
  }

  // Site admin?
  if (compareRole(user.role, "admin")) {
    return true;
  }

  if (org === undefined) {
    if (compareRole(user.role, "manager")) {
      return true;
    }
  }

  // If the user is not in this org it will return null,
  // so this works as a boolean without an explicit comparison.
  const userAndRole = getOrgAndMore(user, org);
  if (compareRole(userAndRole?.role, "breeder")) {
    return true;
  }

  // Non-breeder member, or
  // rando who wandered in off the street and created an account?
  return false;
};

// In which orgs can this user register a dog?
export const userCanRegisterList = () => {
  const user = useAuthenticatedUser();

  if (user === null) {
    return [];
  }

  const organizations = user.organizations
    .map((o) => getOrgAndMore(user, o.organization))
    .filter((userAndRole) => compareRole(userAndRole?.role, "breeder"));

  return organizations;
};

export function isVerified(dog, prefix) {
  const orgAndMore = getOrgAndMoreByPrefix(dog, prefix);
  return (!orgAndMore.metadata.verifiedObject ?
    "unverified" :
    orgAndMore.metadata.verified ? "verified" : "pending");
}

export function isRegistered (dog, prefix) {
  const orgAndMore = getOrgAndMoreByPrefix(dog, prefix);
  return (orgAndMore.regNumber ? "registered" :
    orgAndMore.regRequest ? "pending" : "unregistered");
};

// Is this a registered, pending, or tree dog?
export function getDogType(dog, prefix) {
  if (!dog || !prefix) {
    return "unknown";
  }
  if (isRegistered(dog, prefix) === "registered") {
    return "dog";
  }
  if (isRegistered(dog, prefix) === "pending") {
    return "pendingdog";
  }
  return "treedog";
}

// Extract specified organization+reg object from list of organizations
export function getOrgWithReg(organizations, org) {
  if (!organizations) {
    return null;
  }
  let orgWithReg = organizations.find((o) => o.organization === org?._id);
  if (!orgWithReg) {
    orgWithReg = organizations.find((o) => o.organization._id === org?._id);
  }
  return orgWithReg;
}

export function getLinkDisplay(dog, org) {
  if (!org || !dog || !dog.organizations) {
    return null;
  }

  // We don't know if the organizations array is poplated or not,
  // so check both ID and object.
  let regNum = dog.organizations.find(
    (o) => o._id === org._id || o.organization?._id === org._id || o.organization === org._id,
  )?.regNumber;

  return regNum
    ? {link: regNum, display: regNum, prefix: org.prefix, org: org._id || org}
    : {link: dog._id, display: "", prefix: org.prefix, org: org._id || org};
}

// Try to get kruvalue name by lang, but fallback to en
export function getNameByLang(item, language) {
  if (!item) {
    throw new Error("getNameByLang: item is null");
  }
  return item[language]?.name || item.en.name;
}

export const createHandleInputChange = (setFormData) => {
  return (input, field) => {
    let newValue;
    const {name, type, value, checked, id} = input.target;

    setFormData((prevFormData) => {
      switch (type) {
        case "checkbox":
          if (field) {
            // Handle single checkbox for boolean value
            newValue = checked;
          } else {
            // Handle array of checkboxes
            let oldArray = prevFormData[name] || [];
            if (checked) {
              newValue = oldArray.includes(id) ? oldArray : oldArray.concat(id);
            } else {
              newValue = oldArray.filter((item) => item !== id);
            }
          }
          break;
        case "radio":
          newValue = checked ? value : null;
          break;
        default:
          newValue = value;
      }

      // Could make this more general by searching for just "["
      if (name.startsWith("puppies[")) {
        const match = name.match(/^puppies\[(\d+)\]\.(.+)$/);
        if (!match) {
          throw new Error(`Invalid puppy field name: ${name}`);
        }
        const puppyIndex = parseInt(match[1], 10); // Extract and parse the index
        const puppyField = match[2]; // Extract the field name
        const updatedPuppies = prevFormData.puppies.map((puppy, i) => {
          if (i === puppyIndex) {
            return {
              ...puppy,
              [puppyField]: newValue,
            };
          }
          return puppy;
        });

        return {
          ...prevFormData,
          puppies: updatedPuppies,
        };
      }

      return {
        ...prevFormData,
        [name]: newValue,
      };
    });

    // Call the field's onChange method to update the form state
    if (field && typeof field.onChange === "function") {
      field.onChange(input);
    }
  };
};

export const displayDate = (date) => {
  if (!date) return "";
  // If it is already mm/dd/yyyy, return it
  if (date.length === 10) return date;
  return dayjs(date).tz(TIME_ZONE).format("MM/DD/YYYY");
};

export const stripDate = (e) => {
  // Remove hours and less to avoid timezone problems
  let parsedDate = dayjs(e.target.value).startOf("day");
  if (parsedDate.isValid()) {
    const strippedDate = parsedDate.format("YYYY-MM-DD");
    // Return new event object with the stripped date
    return {
      ...e,
      target: {
        ...e.target,
        value: strippedDate,
        name: e.target.name,
      },
    };
  } else {
    return e;
  }
};

export const validateDateOfBirth = (e, formMethods, t) => {
  const {setError, clearErrors} = formMethods;
  const name = e.target.name;

  const messageInvalidDate = typeof t === "function" ? t("Invalid date") : "Invalid date";
  const messageFuture = typeof t === "function" ? t("Date cannot be in the future") : "Date cannot be in the future";

  let parsedDate = dayjs(e.target.value);
  if (!dayjs(parsedDate).isValid()) {
    setError(name, {
      type: "custom",
      message: messageInvalidDate,
    });
  } else if (dayjs(parsedDate).isAfter(dayjs())) {
    setError(name, {
      type: "custom",
      message: messageFuture,
    });
  } else {
    clearErrors("dateOfBirth");
  }
};

//
// Breed description methods
//

export function getBreeds(kruValues, language) {
  return kruValues
    .filter((i) => i.category === "ancestry" && i._id.startsWith("breeds"))
    .sort((a, b) => getNameByLang(a, language).localeCompare(getNameByLang(b, language)));
}

export function getMixes(kruValues, language) {
  return kruValues
    .filter((i) => i.category === "ancestry" && i._id.startsWith("mixes"))
    .sort((a, b) => getNameByLang(a, language).localeCompare(getNameByLang(b, language)));
}

export function getTypes(kruValues, language) {
  return kruValues
    .filter((i) => i.category === "ancestry" && i._id.startsWith("types"))
    .sort((a, b) => getNameByLang(a, language).localeCompare(getNameByLang(b, language)));
}

export function getRegistries(kruValues, language) {
  return kruValues
    .filter((i) => i.category === "registries")
    .sort((a, b) => getNameByLang(a, language).localeCompare(getNameByLang(b, language)));
}

export function displayDeclaredTypes(dog, kruValues, language) {
  const {t, i18n} = useTranslation();
  const breeds = kruValues.filter((i) => i.category === "ancestry" && i._id.startsWith("breeds"));
  const ancestries = kruValues.filter((i) => i.category === "ancestry");

  if (dog.declaredTypes?.length > 1) {
    return (
      dog.declaredTypes
        .map((type) =>
          getNameByLang(
            ancestries.find((a) => a._id === type),
            language,
          ),
        )
        .join(" / ") + t(" type mix")
    );
  }

  if (dog.declaredTypes?.length == 1) {
    return getNameByLang(
      ancestries.find((a) => a._id === dog.declaredTypes[0]),
      language,
    );
  }

  if (dog.regOther?.length > 0) {
    let deduppedReg = dog.regOther.filter((v, i, a) => a.findIndex((t) => t.regBreed === v.regBreed) === i);
    // Filter out nulls
    deduppedReg = deduppedReg.filter((reg) => reg.regBreed);
    if (deduppedReg.length == 0) {
      return "";
    }
    return deduppedReg
      .map((reg) =>
        getNameByLang(
          breeds.find((b) => b._id === reg.regBreed),
          language,
        ),
      )
      .join(" / ");
  }

  /* HERE: Implement saying something based on ancestry. WAITING on pedigree/genetic testing. */

  return "";
}

export const regOtherToAncestry = (data) => {
  const extRegBreeds = data.map((reg) => reg.regBreed);
  const uniqueBreeds = [...new Set(extRegBreeds)];
  return {
    verification: "extReg",
    breeds: uniqueBreeds.map((b) => {
      return {
        breed: b,
        percentage: 100,
      };
    }),
  };
};

// Image stuff

export const getImageUrl = (dog, size = "full", index=0) => {
  if (dog?.attachments?.images?.length === 0) {
    return defaultImgUrl;
  }
  const fileName = dog?.attachments?.images?.[index]?.fileName;
  if (!fileName) {
    return defaultImgUrl;
  }
  let path = "images";
  if (size !== "full") {
    path = dog.attachments.images[0]?.alternates?.[size]?.path;
  }

  return imageServer + "/" + path + "/" + fileName;
};

export const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous"); // needed to avoid cross-origin issues
    image.src = url;
  });

export const getCroppedImg = async (imageSrc, pixelCrop, fileName) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob((file) => {
      file.name = fileName;
      file.mimetype = "image/jpeg";
      resolve(file);
    }, "image/jpeg");
  });
};

// Coat stuff

export const findCoatData = (obj, field, category) =>
  obj.coat?.[field]?.find((data) => data.startsWith(category)) || "";

export const filterCoatData = (obj, field, category) =>
  obj.coat?.[field]?.filter((data) => data.startsWith(category)) || [];

// Diff stuff

export const getOriginalCoatValue = (originalValue, diffValue, lang = "en", kruValues) => {
  if (originalValue !== diffValue) {
    return <span style={{color: "red"}}>{`Original value: ${getKruValueLabel(originalValue, lang, kruValues) || "not set"}`}</span>;
  }
  return "";
};

export const getOriginalCoatValueArray = (originalValues, diffValues, lang = "en", kruValues) => {
  if (!diffValues) {
    return "";
  }

  const arraysEqual = (arr1, arr2) => {
    if (arr1 && !arr2) return false;
    if (!arr1 && arr2) return false;
    if (arr1.length !== arr2.length) return false;
    const sortedArr1 = arr1.slice().sort();
    const sortedArr2 = arr2.slice().sort();
    return JSON.stringify(sortedArr1) === JSON.stringify(sortedArr2);
  };

  if (!arraysEqual(originalValues, diffValues)) {
    const originalValuesDisplay = originalValues.map((value) => getKruValueLabel(value, lang, kruValues)).join(", ");
    return <span style={{color: "red"}}>{`Original values: ${originalValuesDisplay.length == 0 ? "not set" : originalValuesDisplay}`}</span>;
  }
  return "";
};

export const getOriginalValue = (dog, diff, field, lang = "en", kruValues) => {
  const nestedProperty = (obj, path) => path.split(".").reduce((acc, part) => acc && acc[part], obj);

  if (!diff || !diff.effect) {
    return "";
  }

  let equivalent = true;
  let originalValue = "";

  switch (field) {
    case "sire":
    case "dam":
    case "breeder":
      if (dog[field]?._id !== diff.effect[field]?._id) {
        equivalent = false;
        originalValue = diff.effect[field]?.name;
      }
      break;
    case "owners":
    case "dogOwners":
      const equivalencies = dog[field].map((owner) => {
        return diff.effect[field].find((o) => o._id === owner._id);
      });
      if (equivalencies.includes(undefined) || dog[field].length !== diff.effect[field].length) {
        equivalent = false;
        originalValue = diff.effect[field].map((owner) => owner.name || "(undisclosed name)").join(", ");
      }
      break;
    case "countryOfOrigin":
      if (dog[field] !== diff.effect[field]) {
        equivalent = false;
        originalValue = getCountryByCode(diff?.effect?.countryOfOrigin, lang);
      }
      break;
    case "declaredBreed":
    case "declaredMix":
      if (dog[field] !== diff.effect[field]) {
        equivalent = false;
        originalValue = getKruValueLabel(diff.effect[field], lang, kruValues);
      }
      break;
    case "declaredType1":
      if (dog.declaredTypes[0] !== diff.effect[field]) {
        equivalent = false;
        originalValue = getKruValueLabel(diff.effect[field], lang, kruValues);
      }
      break;
    case "declaredType2":
      if (dog.declaredTypes[1] !== diff.effect[field]) {
        equivalent = false;
        originalValue = getKruValueLabel(diff.effect[field], lang, kruValues);
      }
      break;
    case "regType":
    case "regClass":
      const orgAndMore = getOrgAndMoreByPrefix(dog, diff.effect.organizations[0].prefix);
      const diffOrgAndMore = getOrgAndMoreByPrefix(diff.effect, diff.effect.organizations[0].prefix);
      if (!diffOrgAndMore) break;
      if (orgAndMore[field] !== diffOrgAndMore[field]) {
        equivalent = false;
        originalValue = getKruValueLabel(diffOrgAndMore[field], lang, kruValues);
      }
      break;
    default:
      if (nestedProperty(dog, field) !== nestedProperty(diff.effect, field)) {
        equivalent = false;
        if (field.startsWith("coat.")) {
          originalValue = getKruValueLabel(nestedProperty(diff.effect, field), lang, kruValues);
        } else {
          originalValue = nestedProperty(diff.effect, field);
        }
      }
  }

  if (!equivalent) {
    return <span style={{color: "red"}}>{`Original value: ${originalValue || "not set"}`}</span>;
  }
  return "";
};
